import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { createPopper } from '@popperjs/core';

import { COLORS } from 'ravenjs/constants/colors';
import { callFunc } from 'ravenjs/utils';
import { MIGRATED_COLORS } from 'constants/quartz/migrated_colors';

const TooltipContentStyled = styled.span`
    border-radius: ${({ tooltipStyles }) => tooltipStyles?.borderRadius || '1rem 1rem 1rem 0'};
    border: ${({ tooltipStyles }) => tooltipStyles?.border || `2px solid ${COLORS.BLUE}`};
    background: ${({ tooltipStyles }) => tooltipStyles?.background || COLORS.WHITE};
    color: ${({ tooltipStyles }) => tooltipStyles?.color || COLORS.BLACK};
    height: ${({ tooltipStyles }) => tooltipStyles?.height || 'auto'};
    padding: ${({ tooltipStyles }) => tooltipStyles?.padding || '14px 9px'};
    object-fit: contain;
    box-shadow: ${({ tooltipStyles }) =>
        tooltipStyles?.boxShadow || `2px 2px 12px 0 ${MIGRATED_COLORS.literalRgba00005}`};
    font-family: ${({ tooltipStyles }) =>
        tooltipStyles?.fontFamily || 'Roboto, Arial, Helvetica, sans-serif'};
    font-size: ${({ tooltipStyles }) => tooltipStyles?.fontSize || '16px'};
    font-weight: ${({ tooltipStyles }) => tooltipStyles?.fontWeight || 'bold'};
    font-stretch: normal;
    font-style: normal;
    line-height: ${({ tooltipStyles }) => tooltipStyles?.lineHeight || '1.25'};
    letter-spacing: normal;

    ${({ showArrow, tooltipStyles }) =>
        showArrow &&
        `
        &::before {
            content: '';
            display: block;
            width: 0;
            height: 0;
            position: absolute;
            border-color: ${tooltipStyles?.background ||
                MIGRATED_COLORS.black} transparent transparent transparent;
            border-style: solid;
            border-width: 8px;
            top: 100%;
            left: calc(50% - 8px);
        }
    `}
`;

function CountryStateMap({
    customize,
    defaultFill,
    onClick,
    width,
    height,
    statesData = {},
    selectedFill,
    defaultSelected,
    tooltipStyles,
    multipleSelection,
    showTooltipArrow,
    tooltipFollowCursor,
    showDCLabel,
}) {
    const popperInstance = React.useRef();
    const [currentHovered, setCurrenHovered] = React.useState(null);
    const [currentSelection, setCurrentSelection] = React.useState(defaultSelected);

    const fillStateColor = state => {
        if (multipleSelection) {
            if (customize?.[state]?.fillHandler) {
                return callFunc(customize[state].fillHandler, state);
            }
            if (customize?.[state]?.fill) {
                return customize[state].fill;
            }
            return defaultFill;
        }
        if (selectedFill && state === currentSelection) {
            return selectedFill;
        }
        if (customize && customize[state] && customize[state].fillHandler) {
            return callFunc(customize[state].fillHandler, state);
        }
        if (customize && customize[state] && customize[state].fill) {
            return customize[state].fill;
        }
        return defaultFill;
    };

    const getHoverValue = state => {
        if (customize && customize[state] && customize[state].hoverHandler) {
            return callFunc(customize[state].hoverHandler, state);
        }
        return state;
    };

    const stateClickHandler = state => {
        if (!multipleSelection) {
            setCurrentSelection(state);
        }
        if (customize && customize[state] && customize[state].clickHandler) {
            return customize[state].clickHandler(state);
        }
        return onClick(state);
    };

    function generateGetBoundingClientRect(x = -50, y = -50) {
        return () => ({
            width: 0,
            height: 0,
            top: y,
            right: x,
            bottom: y,
            left: x,
        });
    }

    const virtualElement = {
        getBoundingClientRect: generateGetBoundingClientRect(),
        contextElement: document.getElementById('tracker-tooltip'),
    };

    React.useEffect(() => {
        const popEl = document.getElementById('tracker-tooltip');
        const popperOptions = tooltipFollowCursor
            ? {
                  placement: 'top',
                  modifiers: [
                      {
                          name: 'offset',
                          options: {
                              offset: [0, 0],
                          },
                      },
                  ],
              }
            : {
                  placement: 'top-start',
              };
        popperInstance.current = createPopper(virtualElement, popEl, popperOptions);
    });

    const handleHoverExit = () => {
        virtualElement.getBoundingClientRect = generateGetBoundingClientRect();
        callFunc(popperInstance.current.update);
        setCurrenHovered(null);
    };

    const buildPaths = () => {
        const dcKey = 'DC';
        const paths = [];
        let dcPath = null;

        statesData &&
            Object.keys(statesData).forEach(stateKey => {
                const { dimensions } = statesData[stateKey];
                const fill = fillStateColor(stateKey);

                const pathElement = (
                    <path
                        stroke={COLORS.BLACK}
                        key={stateKey}
                        d={dimensions}
                        fill={fill}
                        data-name={stateKey}
                        className={`${stateKey} state`}
                        onClick={() => {
                            stateClickHandler(stateKey);
                        }}
                        onMouseMove={({ clientX: x, clientY: y }) => {
                            virtualElement.getBoundingClientRect = generateGetBoundingClientRect(
                                x,
                                y - 20
                            );
                            callFunc(popperInstance.current.update);
                            setCurrenHovered(getHoverValue(stateKey));
                        }}
                    />
                );

                if (stateKey === dcKey) {
                    dcPath = pathElement;
                } else {
                    paths.push(pathElement);
                }
            });

        return { paths, dcPath };
    };

    React.useEffect(() => {
        setCurrentSelection(defaultSelected);
    }, [defaultSelected]);

    const { paths, dcPath } = buildPaths();

    const getViewBox = () => {
        return showDCLabel ? '0 0 959 630' : '0 0 959 593';
    };

    const renderDCLabel = () => {
        if (!dcPath || !showDCLabel) return null;

        return (
            <g className="dc-separate">
                <circle
                    cx="800"
                    cy="250"
                    r="4"
                    fill={COLORS.BLACK}
                    stroke="none"
                    style={{ pointerEvents: 'none' }}
                />

                <line
                    x1="803"
                    y1="250"
                    x2="850"
                    y2="250"
                    stroke={COLORS.BLACK}
                    strokeWidth="1"
                    style={{ pointerEvents: 'none' }}
                />

                <line
                    x1="850"
                    y1="250"
                    x2="850"
                    y2="580"
                    stroke={COLORS.BLACK}
                    strokeWidth="1"
                    style={{ pointerEvents: 'none' }}
                />

                <circle
                    cx="745"
                    cy="604"
                    r="12"
                    fill={fillStateColor('DC')}
                    stroke={COLORS.BLACK}
                    strokeWidth="1"
                    style={{ cursor: 'pointer' }}
                    onClick={() => stateClickHandler('DC')}
                    onMouseMove={({ clientX: x, clientY: y }) => {
                        virtualElement.getBoundingClientRect = generateGetBoundingClientRect(
                            x,
                            y - 20
                        );
                        callFunc(popperInstance.current.update);
                        setCurrenHovered(getHoverValue('DC'));
                    }}
                    onMouseOut={() => setCurrenHovered(null)}
                />

                <text
                    x="770"
                    y="610"
                    textAnchor="start"
                    fill={COLORS.BLACK}
                    fontSize="18"
                    fontWeight="normal"
                    fontFamily="Roboto, Arial, Helvetica, sans-serif"
                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                >
                    District of Columbia
                </text>
            </g>
        );
    };

    return (
        <>
            <TooltipContentStyled
                id="tracker-tooltip"
                tooltipStyles={tooltipStyles}
                showArrow={showTooltipArrow}
            >
                {currentHovered}
            </TooltipContentStyled>
            <svg
                onMouseOut={handleHoverExit}
                className="state-map"
                width={width}
                height={height}
                viewBox={getViewBox()}
                preserveAspectRatio="xMidYMid meet"
                style={{ cursor: 'pointer', maxWidth: '100%', height: 'auto' }}
            >
                <g className="outlines">{paths}</g>
                {renderDCLabel()}
            </svg>
        </>
    );
}

CountryStateMap.propTypes = {
    onClick: PropTypes.func.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    defaultFill: PropTypes.string,
    selectedFill: PropTypes.string,
    customize: PropTypes.object,
    statesData: PropTypes.object,
    defaultSelected: PropTypes.string,
    tooltipStyles: PropTypes.object,
    multipleSelection: PropTypes.bool,
    showTooltipArrow: PropTypes.bool,
    tooltipFollowCursor: PropTypes.bool,
    showDCLabel: PropTypes.bool,
};

CountryStateMap.defaultProps = {
    width: 635,
    height: 450,
    defaultFill: COLORS.GREY_3,
    selectedFill: COLORS.BLUE,
    customize: {},
    statesData: {},
    defaultSelected: '',
    tooltipStyles: {},
    multipleSelection: false,
    showTooltipArrow: false,
    tooltipFollowCursor: false,
    showDCLabel: false,
};

export default CountryStateMap;
